import { ReactNode } from 'react';
import {
  Box,
  Heading,
  Link,
  Text,
  OrderedList,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

import NextLink from 'next/link';
import { okaidia } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { ReactMarkdownProps } from 'react-markdown/lib/ast-to-react';
import { slugify } from './utils';

export type CodeComponentProps = JSX.IntrinsicElements['code'] &
  ReactMarkdownProps & {
    inline?: boolean;
  };

export type DefaultRendererProps = {
  children: ReactNode;
};

export const P = ({ children }: DefaultRendererProps) => (
  <Text as="p" mb={4}>
    {children}
  </Text>
);

export const H1 = ({ children }: DefaultRendererProps) => {
  return (
    <Heading as="h1" size="2xl" mb={5} id={slugify(children.toString())}>
      {children}
    </Heading>
  );
};

export const H2 = ({ children }: DefaultRendererProps) => {
  return (
    <Heading as="h2" size="xl" mb={3} id={slugify(children.toString())}>
      {children}
    </Heading>
  );
};
export const H3 = ({ children }: DefaultRendererProps) => {
  return (
    <Heading as="h3" size="lg" mb={3} id={slugify(children.toString())}>
      {children}
    </Heading>
  );
};

export const H4 = ({ children }: DefaultRendererProps) => (
  <Heading as="h4" size="md" mb={3} id={slugify(children.toString())}>
    {children}
  </Heading>
);

export const H5 = ({ children }: DefaultRendererProps) => (
  <Heading as="h5" size="sm" mb={3} id={slugify(children.toString())}>
    {children}
  </Heading>
);

export const H6 = ({ children }: DefaultRendererProps) => (
  <Heading as="h6" size="xs" mb={1} id={slugify(children.toString())}>
    {children}
  </Heading>
);

export const HR = () => (
  <Box mb={4}>
    <hr />
  </Box>
);

export const Code = ({
  children,
  ...rest
}: CodeComponentProps & DefaultRendererProps & { inline: boolean }) => {
  const language = rest.className?.replace(/language-/, '') || 'javascript';

  if (!rest.inline) {
    return (
      <Box mb={4}>
        <SyntaxHighlighter language={language} style={okaidia}>
          {children}
        </SyntaxHighlighter>
      </Box>
    );
  }

  return (
    <Text as="code" color="brand.red">
      {children}
    </Text>
  );
};

export const OL = ({ children }: DefaultRendererProps) => (
  <OrderedList mb={3}>{children}</OrderedList>
);

export const UL = ({ children }: DefaultRendererProps) => (
  <UnorderedList mb={3}>{children}</UnorderedList>
);

export const LI = ({ children }: DefaultRendererProps) => (
  <ListItem>{children}</ListItem>
);

export const A = ({ children, href }: any) => {
  return (
    <NextLink href={href} passHref>
      <Link textDecoration="underline">{children}</Link>
    </NextLink>
  );
};

// export const IMG = ({
//   src,
//   alt,
//   width,
//   height,
//   ...rest
// }: JSX.IntrinsicElements['img']) => {
//   return (
//     <Container position="relative">
//       <Image
//         src={src}
//         alt={alt}
//         layout="responsive"
//         width={width}
//         height={height}
//         {...rest}
//       />
//     </Container>
//   );
// };
